import type { Editor } from '@milkdown/kit/core';
import { editorViewCtx } from '@milkdown/kit/core';
import type { EditorView } from '@milkdown/kit/prose/view';
import { getMarkdown, replaceAll, insert } from '@milkdown/kit/utils';

import { encodeBlockData } from '../core/data-encoding';
import type { EditorAdapter, BlockTypeValue } from '../core/types';

/**
 * Implements EditorAdapter for the Milkdown editor.
 * Uses Milkdown's action system to interact with the editor.
 */
export class MilkdownAdapter implements EditorAdapter {
    private _editor: Editor | null = null;
    private _contentListeners = new Set<(md: string) => void>();
    private _blockTypeListeners = new Set<(type: BlockTypeValue) => void>();
    private _currentBlockType: BlockTypeValue = 'paragraph';

    wireEditor(editor: Editor): void {
        this._editor = editor;
    }

    getMarkdown(): string {
        if (!this._editor) return '';
        try {
            return this._editor.action(getMarkdown());
        } catch {
            return '';
        }
    }

    /**
     * Serialize the current document to markdown, THROWING if the editor is
     * not ready or serialization fails. Unlike getMarkdown(), this never masks
     * a failure as an empty string. Callers that persist content must use this
     * so a serialization error is surfaced (and retried) rather than silently
     * skipped — masking it as '' previously caused invisible data loss, because
     * the auto-save empty-guard then skipped every save while the document
     * still contained un-serialisable content.
     */
    getMarkdownStrict(): string {
        if (!this._editor) {
            throw new Error('Editor is not ready');
        }
        return this._editor.action(getMarkdown());
    }

    setMarkdown(content: string): boolean {
        if (!this._editor) return false;
        try {
            this._editor.action(replaceAll(content));
            return true;
        } catch {
            // Parse/replace failed — let the caller decide how to surface it
            // (e.g. keep the source view open) rather than silently discarding.
            return false;
        }
    }

    insertBlock(blockType: string, data?: unknown): void {
        if (!this._editor) return;
        const encoded = data != null ? encodeBlockData(data) : undefined;
        const md = encoded
            ? `\n\n::${blockType}{data="${encoded}"}\n\n`
            : `\n\n::${blockType}\n\n`;
        try {
            this._editor.action(insert(md));
        } catch {
            // Editor not ready
        }
    }

    insertMarkdown(markdown: string): void {
        if (!this._editor) return;
        try {
            this._editor.action(insert(markdown));
        } catch {
            // Editor not ready
        }
    }

    focus(): void {
        if (!this._editor) return;
        try {
            this._editor.action((ctx) => {
                const view = ctx.get(editorViewCtx);
                view.focus();
            });
        } catch {
            // Editor not ready
        }
    }

    onContentChange(callback: (markdown: string) => void): () => void {
        this._contentListeners.add(callback);
        return () => {
            this._contentListeners.delete(callback);
        };
    }

    /** Called by the editor's listener plugin when markdown changes */
    notifyContentChange(markdown: string): void {
        this._contentListeners.forEach((cb) => cb(markdown));
    }

    applyBlockType(type: BlockTypeValue): void {
        if (!this._editor) return;
        try {
            this._editor.action((ctx) => {
                const view = ctx.get(editorViewCtx);
                const { state, dispatch } = view;
                const { schema, selection } = state;
                const { $from, $to } = selection;

                if (type === 'code') {
                    const codeBlock = schema.nodes.code_block;
                    if (codeBlock) {
                        dispatch(
                            state.tr.setBlockType($from.pos, $to.pos, codeBlock)
                        );
                    }
                } else if (type === 'quote') {
                    const blockquote = schema.nodes.blockquote;
                    if (blockquote) {
                        const { tr } = state;
                        const paragraph = schema.nodes.paragraph.create(
                            null,
                            selection.content().content
                        );
                        tr.replaceSelectionWith(
                            blockquote.create(null, paragraph)
                        );
                        dispatch(tr);
                    }
                } else if (type === 'paragraph') {
                    dispatch(
                        state.tr.setBlockType(
                            $from.pos,
                            $to.pos,
                            schema.nodes.paragraph
                        )
                    );
                } else {
                    // h1-h6
                    const level = parseInt(type.replace('h', ''), 10);
                    const heading = schema.nodes.heading;
                    if (heading) {
                        dispatch(
                            state.tr.setBlockType(
                                $from.pos,
                                $to.pos,
                                heading,
                                { level }
                            )
                        );
                    }
                }

                view.focus();
            });
        } catch {
            // Editor not ready
        }
    }

    getCurrentBlockType(): BlockTypeValue {
        return this._currentBlockType;
    }

    /** Called by the selection watcher to update the current block type */
    updateCurrentBlockType(type: BlockTypeValue): void {
        if (type !== this._currentBlockType) {
            this._currentBlockType = type;
            this._blockTypeListeners.forEach((cb) => cb(type));
        }
    }

    onBlockTypeChange(callback: (type: BlockTypeValue) => void): () => void {
        this._blockTypeListeners.add(callback);
        return () => {
            this._blockTypeListeners.delete(callback);
        };
    }

    /** Run a ProseMirror command on the editor view. Used by the toolbar. */
    runProseMirrorCommand(fn: (view: EditorView) => void): void {
        if (!this._editor) return;
        try {
            this._editor.action((ctx) => {
                const view = ctx.get(editorViewCtx);
                fn(view);
            });
        } catch {
            // Editor not ready
        }
    }

    destroy(): void {
        this._contentListeners.clear();
        this._blockTypeListeners.clear();
        this._editor = null;
    }
}
